import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MerchantTable = ({ data, loading = false, onViewDetails, onViewTransactions }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'merchantName', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedData = React.useMemo(() => {
    const sorted = [...data]?.sort((a, b) => {
      if (sortConfig?.key === 'totalRevenue' || sortConfig?.key === 'totalTransactions' || sortConfig?.key === 'successRate') {
        return sortConfig?.direction === 'asc' 
          ? a?.[sortConfig?.key] - b?.[sortConfig?.key]
          : b?.[sortConfig?.key] - a?.[sortConfig?.key];
      }
      if (sortConfig?.key === 'merchantName') {
        return sortConfig?.direction === 'asc' ? (a?.merchantName ||'')?.localeCompare(b?.merchantName || '')
          : (b?.merchantName || '')?.localeCompare(a?.merchantName || '');
      }
      return 0;
    });
    return sorted;
  }, [data, sortConfig]);

  const paginatedData = sortedData?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(sortedData?.length / itemsPerPage);

  const getStatusBadge = (status) => {
    const configs = {
      active: { color: 'bg-success/10 text-success border-success/20', label: 'Active', icon: 'CheckCircle' },
      pending: { color: 'bg-warning/10 text-warning border-warning/20', label: 'Pending', icon: 'Clock' },
      suspended: { color: 'bg-error/10 text-error border-error/20', label: 'Suspended', icon: 'XCircle' },
      inactive: { color: 'bg-muted text-muted-foreground border-border', label: 'Inactive', icon: 'MinusCircle' }
    };
    const config = configs?.[status] || configs?.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        {config?.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return `Rp ${(amount || 0)?.toLocaleString('id-ID')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date?.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-4 md:p-6 shadow-sm animate-pulse">
        <div className="h-6 bg-muted rounded w-1/3 mb-6"></div>
        <div className="space-y-3">
          {[...Array(5)]?.map((_, i) => (
            <div key={i} className="h-16 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (data?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 shadow-sm text-center">
        <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Merchants Found</h3>
        <p className="text-muted-foreground">Try adjusting your filters or search criteria</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('merchantName')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Merchant
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Business Type</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('totalTransactions')}
                  className="flex items-center gap-2 ml-auto text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Transactions
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('totalRevenue')}
                  className="flex items-center gap-2 ml-auto text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Revenue
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('successRate')}
                  className="flex items-center gap-2 ml-auto text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Success Rate
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Registered</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedData?.map((merchant) => (
              <tr key={merchant?.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-4">
                  <div>
                    <p className="font-medium text-foreground">{merchant?.merchantName}</p>
                    <p className="text-sm text-muted-foreground">{merchant?.email}</p>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-foreground capitalize">{merchant?.businessType || '-'}</span>
                </td>
                <td className="px-4 py-4">
                  {getStatusBadge(merchant?.status)}
                </td>
                <td className="px-4 py-4 text-right">
                  <span className="text-sm font-medium text-foreground">{(merchant?.totalTransactions || 0)?.toLocaleString()}</span>
                </td>
                <td className="px-4 py-4 text-right">
                  <span className="text-sm font-medium text-foreground">{formatCurrency(merchant?.totalRevenue)}</span>
                </td>
                <td className="px-4 py-4 text-right">
                  <span className="text-sm font-medium text-foreground">{(merchant?.successRate || 0)?.toFixed(1)}%</span>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-muted-foreground">{formatDate(merchant?.registrationDate)}</span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(merchant?.id)}
                      className="text-primary hover:text-primary/80"
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewTransactions(merchant?.id)}
                      className="text-primary hover:text-primary/80"
                    >
                      <Icon name="Receipt" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedData?.length)} of {sortedData?.length} merchants
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <Icon name="ChevronLeft" size={16} />
            </Button>
            <span className="text-sm text-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              <Icon name="ChevronRight" size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantTable;